import { PRE_MEMBER_LOGOUT_URL } from 'App/routes/constants';
import { logError } from 'App/services/coralogixService';
import { getMonolithCookie, getUserId, setMonolithCookie } from 'App/services/idpTokenService';
import { MS_API } from 'Shared/services/apiInterface';
import { SwrKeys } from 'Shared/services/swr/swrKeys';
import useSWRMutation from 'swr/mutation';

import { MONOLITH_LOGIN_ENDPOINT } from '../constants';

/**
 * Login user and start a session on the monolith
 * @returns Promise<boolean> - true if login was successful, false otherwise
 */
const loginToMonolith = (() => {
  let   currentRequest: Promise<boolean> | null = null;
  const baseURL                                 = process.env.REACT_APP_MONOLITH_BASE_URL;
  const url                                     = MONOLITH_LOGIN_ENDPOINT;

  return (): Promise<boolean> => {
    if (currentRequest) {
      return currentRequest;
    }

    // Don't call it if the cookie already exists
    if (getMonolithCookie() && getMonolithCookie() === getUserId()) {
      return Promise.resolve(true);
    }

    currentRequest = new Promise<boolean>(resolve => {
      if (!process.env.MONOLITH_SURVEYS_ENABLED) {
        resolve(false);
        return;
      }

      const request = MS_API({
        method         : 'POST',
        baseURL        : baseURL,
        url            : url,
        withCredentials: true
      });

      request.then(() => {
        setMonolithCookie();
        resolve(true);
      });

      request.catch(error => {
        logError('loginToMonolith', error);
        resolve(false);
      });

      request.finally(() => {
        currentRequest = null;
      });
    });

    return currentRequest;
  };
})();

/**
 * Log out of the monolith by calling the logout endpoint.
 * The reason this creates an image instead of calling the
 * endpoint is because /logout doesn't support CORS.
 */
const logoutFromMonolith = (): void => {
  const logoutImage         = document.createElement('img');
  logoutImage.width         = 0;
  logoutImage.height        = 0;
  logoutImage.src           = `${process.env.REACT_APP_MONOLITH_BASE_URL}${PRE_MEMBER_LOGOUT_URL}`;
  logoutImage.style.display = 'none';
};

/**
 * A hook which allows the user to login and start a session on the monolith
 * @returns an SWRMutationResponse where `trigger` logs the user into the monolith
 */
const useLoginToMonolith = () => {
  return useSWRMutation(SwrKeys.MonolithLogin, () => loginToMonolith());
};

export {
  loginToMonolith,
  logoutFromMonolith,
  MONOLITH_LOGIN_ENDPOINT,
  useLoginToMonolith
};
